import React, { useEffect } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng
} from "react-places-autocomplete";
import FilmaizeSelect from "../filmanize-select";
import { GoogleAddressParser } from "./google-address-parser";
import FilmaizeText from "../filmanize-text";
import Errors from "../errors";

const LocationSearchInput = (props) => {

  const { countries,
    defaultCountryId,
    whatThreeWordsLookup,
    label,
    whatThreeWordsAddress,
    whatThreeWordsErrors,
    onUpdatePosition,
    onUpdateAddress,
    searchingWhatThreeWords } = props;

  const [address, setAddress] = React.useState("");
  const [selectedCountry, setSelectedCountry] = React.useState<any>(null);
  const [countryCode, setCountryCode] = React.useState<string>("gb");
  const [whatThreeWords, setWhatThreeWords] = React.useState("");
  const [latitude, setLatitude] = React.useState("");
  const [longitude, setLongitude] = React.useState("");
  const [errors, setErrors] = React.useState<{ [key: string]: string[] } | null>(null);
  const [searchMode, setSearchMode] = React.useState<"text" | "coordinates" | "what3words">("text");

  const countryOptions = (countries || []).map((item) => {
    return { value: item.id, label: item.name };
  });

  useEffect(() => {
    if (defaultCountryId) {
      const foundCountryOption = countryOptions.find((c: any) => c.value == defaultCountryId)
      if (foundCountryOption) {
        setSelectedCountry(foundCountryOption);
      }

      const foundCountry = countries.find((c: any) => c.id == defaultCountryId)
      if (foundCountry) {
        setCountryCode(foundCountry.code);
      }
    }
  }, [defaultCountryId]);

  const reverseGeocodeAndUpdateAddress = (lat, lng) => {
    try {
      // Use Google Maps Geocoder API directly for reverse geocoding      
      const geocoder = new (window as any).google.maps.Geocoder();
      const latLng = { lat, lng };

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          // Find the most detailed result: street_address > premise > locality
          let bestResult = results.find(r => r.types.includes('street_address'));
          if (!bestResult) {
            bestResult = results.find(r => r.types.includes('premise'));
          }
          if (!bestResult) {
            bestResult = results.find(r => r.types.includes('postal_code'));
          }

          if (!bestResult) {
            bestResult = results.find(r => r.types.includes('locality'));
          }
          const result = bestResult || results[0];

          const name = result.formatted_address.split(",")[0];
          const address = new GoogleAddressParser(
            name,
            result.address_components,
            lat,
            lng,
            result.place_id
          ).result();

          onUpdatePosition({ lat, lng });
          onUpdateAddress(address);
          setAddress(result.formatted_address);
        } else {
          console.error("Geocoder failed:", status);
          setErrors({ geocoding: ["Failed to find address for the given coordinates"] });
        }
      });
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setErrors({ geocoding: ["Failed to find address for the given coordinates"] });
    }
  };

  useEffect(() => {
    if (whatThreeWordsAddress) {
      reverseGeocodeAndUpdateAddress(whatThreeWordsAddress.latitude, whatThreeWordsAddress.longitude);
    }
  }, [whatThreeWordsAddress]);

  const handleSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const latLng = await getLatLng(results[0]);

    const name = value.split(",")[0];
    const address = new GoogleAddressParser(
      name,
      results[0].address_components,
      latLng.lat,
      latLng.lng,
      results[0].place_id
    ).result();

    onUpdatePosition(latLng);
    onUpdateAddress(address);
    setAddress(value);
  };

  const searchWhatThreeWords = async (e) => {
    e.preventDefault();
    setErrors(null);

    if (!whatThreeWords || whatThreeWords.trim() === "") {
      setErrors({ what3words: ["Please enter What3Words address"] });
      return;
    }

    // Remove optional /// prefix and validate
    const cleanedInput = whatThreeWords.trim().replace(/^\/\/\//, '');

    // What3Words validation: exactly 3 words separated by dots, each word can contain letters and hyphens
    const what3wordsRegex = /^[a-zA-Z]+(-[a-zA-Z]+)*\.[a-zA-Z]+(-[a-zA-Z]+)*\.[a-zA-Z]+(-[a-zA-Z]+)*$/;

    if (!what3wordsRegex.test(cleanedInput)) {
      setErrors({ what3words: ["Invalid What3Words format. Please enter exactly 3 words separated by dots (e.g., filled.count.soap or ///filled.count.soap)"] });
      return;
    }

    try {
      whatThreeWordsLookup(cleanedInput);

    } catch (error) {
      console.error("Error looking up What3Words:", error);
      setErrors({ what3words: ["Failed to lookup What3Words address"] });
    }
  }

  const handleLatLngSearch = async (e) => {
    e.preventDefault();

    setErrors(null);

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      setErrors({ coordinates: ["Please enter valid latitude and longitude values"] });
      return;
    }

    if (lat < -90 || lat > 90) {
      setErrors({ latitude: ["Latitude must be between -90 and 90"] });
      return;
    }

    if (lng < -180 || lng > 180) {
      setErrors({ longitude: ["Longitude must be between -180 and 180"] });
      return;
    }

    reverseGeocodeAndUpdateAddress(lat, lng);
  };

  const onSetAddress = (value) => {
    setAddress(value);
  };

  const searchOptions = {
    componentRestrictions: { country: [countryCode] }
  };

  const onChangeCountry = (country) => {
    const foundCountry = countries.find((c: any) => c.id == country.value)
    if (foundCountry) {
      setCountryCode(foundCountry.code);
    }

    setSelectedCountry(country);
  };



  const readOnly = props.readOnly;

  return (
    <div>
      <div className="tabs">
        <ul>
          <li className={searchMode === "text" ? "is-active" : ""}>
            <a onClick={() => { if (!searchingWhatThreeWords) { setSearchMode("text"); setErrors(null); } }}
              style={{ cursor: searchingWhatThreeWords ? 'not-allowed' : 'pointer', opacity: searchingWhatThreeWords ? 0.5 : 1 }}>
              Search by Address
            </a>
          </li>
          <li className={searchMode === "coordinates" ? "is-active" : ""}>
            <a onClick={() => { if (!searchingWhatThreeWords) { setSearchMode("coordinates"); setErrors(null); } }}
              style={{ cursor: searchingWhatThreeWords ? 'not-allowed' : 'pointer', opacity: searchingWhatThreeWords ? 0.5 : 1 }}>
              Search by Coordinates
            </a>
          </li>
          <li className={searchMode === "what3words" ? "is-active" : ""}>
            <a onClick={() => { if (!searchingWhatThreeWords) { setSearchMode("what3words"); setErrors(null); } }}
              style={{ cursor: searchingWhatThreeWords ? 'not-allowed' : 'pointer', opacity: searchingWhatThreeWords ? 0.5 : 1 }}>
              Search by What3Words
            </a>
          </li>
        </ul>
      </div>

      {searchMode === "text" && (
        <>
          <PlacesAutocomplete
            value={address}
            onChange={onSetAddress}
            onSelect={handleSelect}
            searchOptions={searchOptions}>
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <div className="field">
                <label className="label">{label ? label : "Search"}</label>
                <input
                  className="control input"
                  {...getInputProps({
                    placeholder: "Start typing any part of the address to search..."
                  })}
                />
                {loading ? <div>...loading</div> : null}

                {suggestions.map((suggestion, idx) => {
                  const style = {
                    backgroundColor: suggestion.active ? "#00FFCE" : "#222E44",
                    color: suggestion.active ? "#040F21" : "#ffffff",
                    padding: "0.4rem"
                  };

                  return (
                    <div key={idx} {...getSuggestionItemProps(suggestion, { style })}>
                      {suggestion.description}
                    </div>
                  );
                })}
              </div>
            )}
          </PlacesAutocomplete>
          <FilmaizeSelect
            value={selectedCountry}
            placeholder="Country"
            isMulti={false}
            options={countryOptions}
            className="react-select-control"
            classNamePrefix="react-select"
            onChange={onChangeCountry}
            isDisabled={readOnly}
          />
        </>
      )}
      {searchMode === "coordinates" && (
        <div >
          <Errors errors={errors} />
          <div className="field is-grouped">
            <div className="control is-expanded">
              <FilmaizeText
                label="Latitude"
                value={latitude}
                onChange={(value) => setLatitude(String(value ?? ''))}
                placeholder="e.g., 51.5074"
                disabled={readOnly}
              />
            </div>
            <div className="control is-expanded">
              <FilmaizeText
                label="Longitude"
                value={longitude}
                onChange={(value) => setLongitude(String(value ?? ''))}
                placeholder="e.g., -0.1278"
                disabled={readOnly}
              />
            </div>
          </div>
          <button
            className="button is-primary"
            onClick={handleLatLngSearch}
            disabled={readOnly || !latitude || !longitude}
          >
            Find Address
          </button>
        </div>
      )}

      {searchMode === "what3words" && (
        <div>
          <Errors errors={errors || whatThreeWordsErrors} />
          <FilmaizeText
            label="What3Words"
            value={whatThreeWords}
            onChange={(value) => setWhatThreeWords(String(value ?? ''))}
            placeholder="e.g., filled.count.soap"
            readOnly={readOnly || searchingWhatThreeWords}
          />
          <button
            className={"button is-primary mt-1" + (searchingWhatThreeWords ? " is-loading" : "")}
            onClick={searchWhatThreeWords}
            disabled={readOnly || !whatThreeWords}
          >
            Find Address
          </button>
        </div>
      )}

      {/* <div className="mt-2">
        <FilmaizeText label="What Three Words" value={whatThreeWords} onChange={setWhatThreeWords} />
        <button className="button" onClick={searchWhatThreeWords}>Find What Three Words</button>
      </div> */}
    </div>
  );
};

export default LocationSearchInput;
